import { Fragment, useCallback, useEffect, useState } from 'react';
import {
    AlertTriangle, AlertCircle, CheckCircle2, HelpCircle, Loader2, ChevronRight,
} from 'lucide-react';
import { useVatLang, useProblemKeyExpander } from './VatLang';
import { vatReportingApi } from '../../services/vatReportingApi';
import { EntryDrillModal } from './EntryDrillModal';
import { GlPostingsModal } from './GlPostingsModal';
import { FilingStatusBar } from './FilingStatusBar';
import { CarryForwardPanel } from './CarryForwardPanel';
import { EcSalesPanel, IntrastatPanel } from './DeclarationPanels';
import type {
    GlAccountMovement, KennzahlDerivationStep, VatCheck, VatCheckStatus, VatCompany,
    VatReturnResponse,
} from '../../types/vatReporting';

/**
 * The `accounts` param on GL_RECONCILIATION_INPUT/OUTPUT is the same `accounts.join(', ')`
 * string checks.ts builds for the message text - reused here rather than adding a field, so the
 * check and the button behind it can never name a different account list.
 */
function glMovementsForCheck(check: VatCheck, glMovements: GlAccountMovement[]): GlAccountMovement[] {
    const accountsParam = check.params?.accounts;
    if (typeof accountsParam !== 'string') return [];
    const wanted = new Set(accountsParam.split(',').map(s => s.trim()).filter(Boolean));
    return glMovements.filter(m => wanted.has(m.glAccountNo));
}

/**
 * The monthly German VAT package for one company.
 *
 * Four panels over one month: the Kennzahlen with a drilldown to the transactions, the checks,
 * the EC Sales List and Intrastat with their import files. They share one fetch because several
 * checks compare two of them against each other - splitting the request would let the page show
 * a reconciliation between figures taken at different moments.
 */

interface Props {
    company: VatCompany;
    year: number;
    month: number;
    canEdit: boolean;
    onError: (message: string) => void;
}

const money = (v: number | null | undefined, dp = 2) =>
    v === null || v === undefined
        ? '—'
        : new Intl.NumberFormat('de-CH', { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(v);

const whole = (v: number | null | undefined) =>
    v === null || v === undefined ? '—' : new Intl.NumberFormat('de-CH').format(v);

export function UstvaDeTab({ company, year, month, canEdit, onError }: Props) {
    const { t } = useVatLang();
    const [data, setData] = useState<VatReturnResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [drill, setDrill] = useState<{
        title: string; subtitle?: string; filter: Record<string, unknown>;
    } | null>(null);
    const [glOpen, setGlOpen] = useState<GlAccountMovement[] | null>(null);
    // Bumped only by a manual decision, not by the ordinary reload: the ZM and Intrastat panels
    // fetch their own endpoints and would otherwise still be showing the month as it stood
    // before the posting was taken out of it or carried into it.
    const [reloadKey, setReloadKey] = useState(0);

    const load = useCallback(() => {
        setLoading(true);
        vatReportingApi.getReturn({ company, year, month })
            .then(setData)
            .catch(e => onError(e instanceof Error ? e.message : t('panel.loadFailedReturn')))
            .finally(() => setLoading(false));
    }, [company, year, month, onError]);

    /** Something changed which postings this month declares. Everything on the tab reloads. */
    const reloadAll = useCallback(() => {
        setReloadKey(k => k + 1);
        load();
    }, [load]);

    useEffect(load, [load]);

    if (loading && !data) {
        return (
            <div className="p-12 text-center text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin inline mr-2" />{t('page.loading')}
            </div>
        );
    }
    if (!data) return null;

    const noReportingCurrency = data.reportingCurrency === null;

    return (
        <div className="space-y-4">
            <FilingStatusBar data={data} company={company} year={year} month={month}
                             canEdit={canEdit} onChanged={load} onError={onError} />

            {noReportingCurrency && (
                <div className="flex items-start gap-2 border border-amber-200 bg-amber-50 rounded-lg p-3 text-sm text-amber-900">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                        <strong>{company.toUpperCase()}</strong>{' '}
                        {t('check.REPORTING_CURRENCY.unavailable')}
                    </div>
                </div>
            )}

            <ChecksPanel checks={data.checks}
                         onDrill={(c) => setDrill({
                             title: c.title,
                             subtitle: c.detail,
                             // A control's evidence has to include what was excluded by hand -
                             // MANUAL_EXCLUSIONS is a finding ABOUT those rows, and a drilldown
                             // that dropped them would open empty. Safe here and nowhere else:
                             // this list is filtered to a finding's entry numbers, not summed
                             // against a Kennzahl.
                             filter: {
                                 entryNos: c.entryNos,
                                 includeSettlement: true,
                                 includeExcluded: true,
                             },
                         })}
                         onShowGl={(c) => {
                             const movements = glMovementsForCheck(c, data.glMovements);
                             if (movements.length > 0) setGlOpen(movements);
                         }} />

            {/* Only when there are some (plan 5.4). The zero case is not silent - the
                CARRY_FORWARD_OPEN control is on the page either way and says so. */}
            {data.exclusions.carryForward.length > 0 && (
                <CarryForwardPanel company={company} year={year} month={month}
                                   entries={data.exclusions.carryForward}
                                   canEdit={canEdit}
                                   periodLocked={data.period?.status === 'filed'}
                                   onChanged={reloadAll} />
            )}

            {!noReportingCurrency && (
                <KennzahlenPanel data={data}
                                 onDrill={(kz, label) => setDrill({
                                     title: `Kz ${kz}`, subtitle: label, filter: { kennzahl: kz },
                                 })}
                                 onDrillGroup={(bus, prod, direction) => setDrill({
                                     title: `${bus} / ${prod}`,
                                     subtitle: direction,
                                     filter: {
                                         vatBusPostingGroup: bus,
                                         vatProdPostingGroup: prod,
                                         direction,
                                     },
                                 })} />
            )}

            <GroupTotalsPanel data={data}
                              onDrill={(g) => setDrill({
                                  title: `${g.vatBusPostingGroup} / ${g.vatProdPostingGroup}`,
                                  subtitle: `${g.direction} · ${t('drill.count', { count: g.entryCount })}`,
                                  filter: {
                                      vatBusPostingGroup: g.vatBusPostingGroup,
                                      vatProdPostingGroup: g.vatProdPostingGroup,
                                      direction: g.direction,
                                  },
                              })} />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <EcSalesPanel company={company} year={year} month={month}
                              reloadKey={reloadKey} onError={onError}
                              onDrill={(vatId, entryNos) => setDrill({
                                  title: `${t('ec.title')} — ${vatId}`,
                                  filter: { entryNos },
                              })} />
                <IntrastatPanel company={company} year={year} month={month}
                                reloadKey={reloadKey} onError={onError} />
            </div>

            <DeltaPanel data={data}
                        onDrill={(kind, entryNos) => setDrill({
                            title: t('delta.drillTitle', { kind: t(`delta.${kind}`) }),
                            filter: { entryNos },
                        })} />

            {drill && (
                <EntryDrillModal isOpen onClose={() => setDrill(null)}
                                 company={company} year={year} month={month}
                                 title={drill.title} subtitle={drill.subtitle}
                                 filter={drill.filter}
                                 canEdit={canEdit}
                                 onChanged={reloadAll} />
            )}

            {glOpen && (
                <GlPostingsModal movements={glOpen} company={company} onClose={() => setGlOpen(null)} />
            )}
        </div>
    );
}

// ---------------------------------------------------------------- Kennzahlen ----

/**
 * The rungs between "what the data says" and "what goes on the form".
 *
 * This is the part the old workbook did by being a workbook: every contributing row was
 * physically in the sheet, so the SUBTOTAL was self-explanatory. A page that shows only the
 * final figure asks to be trusted; this shows the same steps, in place, without an overlay.
 */
function DerivationLadder({ steps, onDrillGroup, onDrillKennzahl }: {
    steps: KennzahlDerivationStep[];
    onDrillGroup: (bus: string, prod: string, direction: string) => void;
    onDrillKennzahl: (kennzahl: string) => void;
}) {
    const { t, tOr } = useVatLang();
    if (steps.length === 0) return null;

    // The server labels a rung in English; a 'group' rung is a posting-group name and stays as
    // it is in both languages. Anything else has a key, and the English label is the fallback.
    const rung = (s: KennzahlDerivationStep) => {
        if (s.kind === 'group') return s.label;
        if (s.kind === 'raw') return t('deriv.sum');
        if (s.kind === 'rate') return t('deriv.rate');
        if (s.kind === 'rounded') {
            return s.note === 'input tax is never rounded' ? t('deriv.toTheCent') : t('deriv.truncated');
        }
        return tOr(`deriv.${s.kind}`, s.label);
    };
    const rungNote = (s: KennzahlDerivationStep) => {
        if (!s.note) return undefined;
        const map: Record<string, string> = {
            'sign reversed - BC posts sales negative': 'deriv.signReversed',
            'nothing to drop': 'deriv.nothingDropped',
            'input tax is never rounded': 'deriv.inputNotRounded',
            'negative = a refund': 'deriv.refund',
        };
        if (map[s.note]) return t(map[s.note]);
        if (s.note.includes('zugunsten des Unternehmers')) {
            return t('deriv.dropped', { amount: s.note.split(' ')[0] });
        }
        if (s.note.startsWith('computed from the rounded base')) return t('deriv.fromRoundedBase');
        return s.note;
    };
    return (
        <table className="w-full text-sm">
            <tbody>
                {steps.map((s, i) => {
                    const clickable = (s.kind === 'group' && s.vatBusPostingGroup !== undefined)
                        || (s.kind === 'component' && s.kennzahl);
                    const isResult = s.kind === 'tax' || s.kind === 'rounded';
                    return (
                        <tr key={i} className={isResult ? 'font-medium text-gray-900' : 'text-gray-600'}>
                            <td className="py-1 pl-10 pr-3 w-8 text-gray-400">
                                {s.kind === 'group' ? '+' : s.kind === 'component' ? '' : '='}
                            </td>
                            <td className="py-1 pr-3">
                                {clickable ? (
                                    <button
                                        className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                                        onClick={() => s.kind === 'group'
                                            ? onDrillGroup(s.vatBusPostingGroup!, s.vatProdPostingGroup ?? '', s.direction ?? '')
                                            : onDrillKennzahl(s.kennzahl!)}>
                                        {rung(s)}
                                        {s.entryCount !== undefined && (
                                            <span className="ml-1 text-xs text-gray-400">
                                                {t('deriv.items', { count: s.entryCount })}
                                            </span>
                                        )}
                                    </button>
                                ) : rung(s)}
                                {s.note && <div className="text-xs text-gray-400">{rungNote(s)}</div>}
                            </td>
                            <td className="py-1 pr-4 text-right tabular-nums whitespace-nowrap w-40">
                                {s.kind === 'rate' ? `${s.value} %` : money(s.value)}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

function KennzahlenPanel({ data, onDrill, onDrillGroup }: {
    data: VatReturnResponse;
    onDrill: (kennzahl: string, label: string) => void;
    onDrillGroup: (bus: string, prod: string, direction: string) => void;
}) {
    const { t } = useVatLang();
    const [expanded, setExpanded] = useState<string | null>(null);
    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50 flex items-baseline justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('ustva.title', { period: `${data.year}-${String(data.month).padStart(2, '0')}` })}
                </h3>
                <span className="text-xs text-gray-500">
                    Steuernummer {data.filingIdentity.taxNumber} · {data.filingIdentity.vatRegistrationNo}
                    {data.filingIdentity.taxOffice
                        && ` · ${t('ustva.taxOffice')} ${data.filingIdentity.taxOffice}`}
                </span>
            </div>
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-xs uppercase text-gray-500 border-b">
                        <th className="px-4 py-2 text-left w-16">{t('ustva.kz')}</th>
                        <th className="px-4 py-2 text-left">{t('ustva.label')}</th>
                        <th className="px-4 py-2 text-right w-40">{t('ustva.raw')}</th>
                        <th className="px-4 py-2 text-right w-40">{t('ustva.reported')}</th>
                        <th className="px-4 py-2 text-right w-24">{t('ustva.items')}</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {data.kennzahlen.map(k => {
                        const rounded = !k.isTaxAmount && Math.abs(k.rawValue - k.reportedValue) >= 0.005;
                        const isTotal = k.kennzahl === '83';
                        const steps = data.derivations?.[k.kennzahl] ?? [];
                        const isOpen = expanded === k.kennzahl;
                        return (
                            <Fragment key={k.kennzahl}>
                                <tr className={isTotal ? 'bg-gray-50 font-semibold' : 'hover:bg-blue-50/40'}>
                                    <td className="px-4 py-2 font-mono">
                                        {/* Every figure opens its own arithmetic. Kz 83 too - it used
                                            to say only "berechnet", which is the one word a reviewer
                                            cannot check. */}
                                        <button
                                            onClick={() => setExpanded(isOpen ? null : k.kennzahl)}
                                            disabled={steps.length === 0}
                                            title={steps.length === 0 ? t('ustva.noDerivation') : t('ustva.showDerivation')}
                                            className={`inline-flex items-center gap-1 ${steps.length ? 'hover:text-blue-700' : 'text-gray-400 cursor-default'}`}>
                                            <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-90' : ''} ${steps.length ? '' : 'opacity-0'}`} />
                                            {k.kennzahl}
                                        </button>
                                    </td>
                                    <td className="px-4 py-2 text-gray-700">{k.label}</td>
                                    <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                                        {money(k.rawValue)}
                                        {rounded && (
                                            <span className="ml-1 text-xs text-gray-400"
                                                  title={t('ustva.truncatedHint')}>
                                                ↓ {t('ustva.truncatedBy', { amount: money(k.rawValue - k.reportedValue) })}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-right tabular-nums">
                                        {k.isTaxAmount ? money(k.reportedValue) : whole(k.reportedValue)}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                        {k.entryCount > 0 ? (
                                            <button onClick={() => onDrill(k.kennzahl, k.label)}
                                                    className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 tabular-nums">
                                                {k.entryCount}<ChevronRight className="h-3.5 w-3.5" />
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 tabular-nums"
                                                  title={isTotal ? t('ustva.derivedFromOthers') : t('ustva.noTransactions')}>
                                                {isTotal ? t('ustva.computed') : '0'}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                {isOpen && (
                                    <tr className="bg-blue-50/30">
                                        <td colSpan={5} className="px-0 py-2 border-t border-blue-100">
                                            <DerivationLadder
                                                steps={steps}
                                                onDrillGroup={onDrillGroup}
                                                onDrillKennzahl={kz => onDrill(kz, `Kz ${kz}`)} />
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
            <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-600">
                {t('ustva.footnotePrefix', {
                    outputTax: money(data.outputTax),
                    breakdown: data.outputTaxBreakdown.length > 0
                        ? ` (${data.outputTaxBreakdown.map(b =>
                            `Kz ${b.kennzahl} ${whole(b.base)} × ${b.ratePct} % = ${money(b.tax)}`).join(' + ')})`
                        : '',
                    deductibleTax: money(data.deductibleTax),
                })}
                {' '}<strong>{money(data.settlementAmount)}</strong>.{' '}
                {t('ustva.footnoteSuffix')}
            </div>
        </div>
    );
}

// -------------------------------------------------------------------- checks ----

const CHECK_ICON: Record<VatCheckStatus, JSX.Element> = {
    ok: <CheckCircle2 className="h-4 w-4 text-green-600" />,
    warning: <AlertTriangle className="h-4 w-4 text-amber-600" />,
    error: <AlertCircle className="h-4 w-4 text-red-600" />,
    unavailable: <HelpCircle className="h-4 w-4 text-gray-400" />,
};

const GL_CHECK_CODES = new Set(['GL_RECONCILIATION_INPUT', 'GL_RECONCILIATION_OUTPUT']);

function ChecksPanel({ checks, onDrill, onShowGl }: {
    checks: VatCheck[];
    onDrill: (check: VatCheck) => void;
    onShowGl: (check: VatCheck) => void;
}) {
    const { t, tOr } = useVatLang();
    const expandProblems = useProblemKeyExpander();
    const [showAll, setShowAll] = useState(false);
    const problems = checks.filter(c => c.status !== 'ok');
    const shown = showAll ? checks : problems;

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('controls.title')}
                    <span className="ml-2 font-normal text-gray-500">
                        {problems.length === 0
                            ? t('controls.allPassed', { count: checks.length })
                            : t('controls.needAttention', { problems: problems.length, count: checks.length })}
                    </span>
                </h3>
                <button onClick={() => setShowAll(v => !v)}
                        className="text-xs text-blue-600 hover:text-blue-800">
                    {showAll ? t('controls.showFindings') : t('controls.showAll')}
                </button>
            </div>
            {shown.length === 0 ? (
                <div className="px-4 py-6 text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    {t('controls.everyPassed')}
                </div>
            ) : (
                <ul className="divide-y divide-gray-100">
                    {shown.map(c => (
                        <li key={c.code} className="px-4 py-2.5 flex items-start gap-3">
                            <span className="mt-0.5 shrink-0">{CHECK_ICON[c.status]}</span>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">
                                    {tOr(`checkTitle.${c.code}`, c.title, c.params)}
                                </div>
                                <div className="text-sm text-gray-600">
                                    {expandProblems(
                                        tOr(c.messageKey ?? `check.${c.code}.${c.status}`, c.detail, c.params))}
                                </div>
                            </div>
                            {c.entryNos && c.entryNos.length > 0 && (
                                <button onClick={() => onDrill(c)}
                                        className="shrink-0 text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap">
                                    {t('controls.transactions',
                                        { count: `${c.entryNos.length}${c.truncated ? '+' : ''}` })}
                                </button>
                            )}
                            {GL_CHECK_CODES.has(c.code) && (
                                <button onClick={() => onShowGl(c)}
                                        title={t('gl.showPostings')}
                                        className="shrink-0 text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap">
                                    {t('gl.showPostings')}
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// -------------------------------------------------------------- group totals ----

function GroupTotalsPanel({ data, onDrill }: {
    data: VatReturnResponse;
    onDrill: (g: VatReturnResponse['groupTotals'][number]) => void;
}) {
    const { t } = useVatLang();
    const showReporting = data.reportingCurrency !== null;
    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('groups.title')}
                    <span className="ml-2 font-normal text-gray-500">{t('groups.subtitle')}</span>
                </h3>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-xs uppercase text-gray-500 border-b">
                            <th className="px-4 py-2 text-left">{t('groups.busGroup')}</th>
                            <th className="px-4 py-2 text-left">{t('groups.prodGroup')}</th>
                            <th className="px-4 py-2 text-left">{t('groups.direction')}</th>
                            <th className="px-4 py-2 text-left">{t('ustva.kz')}</th>
                            <th className="px-4 py-2 text-right">{t('drill.baseLocal')}</th>
                            <th className="px-4 py-2 text-right">{t('drill.taxLocal')}</th>
                            {showReporting && <th className="px-4 py-2 text-right">{t('drill.baseReporting')}</th>}
                            {showReporting && <th className="px-4 py-2 text-right">{t('drill.taxReporting')}</th>}
                            <th className="px-4 py-2 text-right">{t('ustva.items')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {data.groupTotals.map(g => {
                            const bad = g.disposition === 'unmapped' || g.disposition === 'pending';
                            return (
                                <tr key={`${g.vatBusPostingGroup}|${g.vatProdPostingGroup}|${g.direction}`}
                                    className={bad ? 'bg-red-50/60' : 'hover:bg-gray-50'}>
                                    <td className="px-4 py-1.5">{g.vatBusPostingGroup}</td>
                                    <td className="px-4 py-1.5">{g.vatProdPostingGroup || <em className="text-gray-400">{t('groups.empty')}</em>}</td>
                                    <td className="px-4 py-1.5 text-gray-600">{g.direction}</td>
                                    <td className="px-4 py-1.5">
                                        {g.kennzahl
                                            ? <span className="font-mono">{g.kennzahl}</span>
                                            : <span className={`text-xs ${bad ? 'text-red-700' : 'text-gray-500'}`}
                                                    title={g.note ?? undefined}>
                                                {g.disposition === 'not_reported' ? t('groups.notReported') : g.disposition}
                                              </span>}
                                    </td>
                                    <td className="px-4 py-1.5 text-right tabular-nums">{money(g.baseLocal)}</td>
                                    <td className="px-4 py-1.5 text-right tabular-nums">{money(g.taxLocal)}</td>
                                    {showReporting && <td className="px-4 py-1.5 text-right tabular-nums">{money(g.baseReporting)}</td>}
                                    {showReporting && <td className="px-4 py-1.5 text-right tabular-nums">{money(g.taxReporting)}</td>}
                                    <td className="px-4 py-1.5 text-right">
                                        <button onClick={() => onDrill(g)}
                                                className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 tabular-nums">
                                            {g.entryCount}<ChevronRight className="h-3.5 w-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {data.settlement.posted && (
                <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-600">
                    {t('groups.settlementNote', {
                        docs: data.settlement.documentNos.join(', '),
                        amount: money(data.settlement.amountLocal),
                    })}
                </div>
            )}
        </div>
    );
}

// -------------------------------------------------------- postings after filing ----

function DeltaPanel({ data, onDrill }: {
    data: VatReturnResponse;
    onDrill: (kind: string, entryNos: number[]) => void;
}) {
    const { t } = useVatLang();
    const d = data.delta;
    if (!d.available) return null;
    const total = d.added.length + d.changed.length + d.removed.length;

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('delta.title')}
                    <span className="ml-2 font-normal text-gray-500">
                        {t('delta.subtitle', {
                            count: d.snapshotEntryCount,
                            date: d.snapshotTakenAt
                                ? new Date(d.snapshotTakenAt).toLocaleDateString('de-CH') : '—',
                        })}
                    </span>
                </h3>
            </div>
            {total === 0 ? (
                <div className="px-4 py-4 text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    {t('delta.nothingMoved')}
                </div>
            ) : (
                <div className="px-4 py-3 space-y-2">
                    <div className="flex flex-wrap gap-4 text-sm">
                        {([['added', d.added], ['changed', d.changed], ['removed', d.removed]] as const)
                            .filter(([, list]) => list.length > 0)
                            .map(([kind, list]) => (
                                <button key={kind} onClick={() => onDrill(kind, list.map(e => e.entryNo))}
                                        className="px-3 py-1.5 border rounded hover:bg-gray-50">
                                    <span className="font-semibold">{list.length}</span>{' '}
                                    <span className="text-gray-600">{t(`delta.${kind}`)}</span>
                                </button>
                            ))}
                    </div>
                    {d.kennzahlImpact.length > 0 && (
                        <p className="text-sm text-gray-700">
                            {t('delta.effect', {
                                list: d.kennzahlImpact.map(k =>
                                    `Kz ${k.kennzahl} ${k.delta > 0 ? '+' : ''}${money(k.delta)}`).join(', '),
                            })}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
